import { useUpdate, useList } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/providers/api-client';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormField, FormStickyActions, FormHeader, FormSpacer } from '@/components/forms';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import {
  TranslationsForm,
  LanguageSwitcher,
  createEmptyTranslationData,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import { Loader2 } from 'lucide-react';
import type { ClubSettings, ClubSettingsFormData, ClubSettingsTranslation, ClubStats, ClubSphere } from './types';

// ─── Translation field groups ──────────────────────────────

const BANNER_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Заголовок баннера', type: 'input', required: true },
  { name: 'subtitle', label: 'Подзаголовок баннера', type: 'textarea', rows: 3 },
];

const MISSION_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'missionTitle', label: 'Заголовок блока миссии', type: 'input', placeholder: 'Миссия клуба' },
  { name: 'mission', label: 'Текст миссии (цитата)', type: 'richtext' },
];

const STATS_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'statsResearchLabel', label: 'Исследований — заголовок', type: 'input', placeholder: 'Исследований' },
  { name: 'statsResearchDescription', label: 'Исследований — подпись', type: 'input', placeholder: 'социальных наблюдений' },
  { name: 'statsPracticeLabel', label: 'Часы практики — заголовок', type: 'input', placeholder: 'Часов практики' },
  { name: 'statsPracticeDescription', label: 'Часы практики — подпись', type: 'input', placeholder: 'работы психологов' },
  { name: 'statsSpecialistsLabel', label: 'Специалисты — заголовок', type: 'input', placeholder: 'Аналитика и методика' },
  { name: 'statsSpecialistsDescription', label: 'Специалисты — подпись', type: 'input', placeholder: 'часов работы специалистов' },
  { name: 'statsCountriesLabel', label: 'Страны — заголовок', type: 'input', placeholder: 'Страны присутствия' },
  { name: 'statsCountriesDescription', label: 'Страны — подпись', type: 'input', placeholder: 'международное сообщество' },
];

const SPHERES_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'spheresTitle', label: 'Заголовок блока сфер', type: 'input', placeholder: 'Сферы жизни' },
  { name: 'spheresDescription', label: 'Описание блока сфер (поддерживает HTML)', type: 'richtext' },
];

const EXPERIENCE_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'experienceTitle', label: 'Заголовок блока опыта', type: 'input', placeholder: 'Опыт клуба' },
  { name: 'experienceSubtitle', label: 'Подпись (единица измерения)', type: 'input', placeholder: 'Лет' },
];

const METHODOLOGY_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'methodology', label: 'Название методологии', type: 'input' },
  { name: 'methodologyDescription', label: 'Описание методологии', type: 'textarea', rows: 3 },
];

const DESCRIPTION_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'description', label: 'Описание клуба', type: 'richtext' },
];

/** Все поля — для LanguageSwitcher (индикация заполненности) */
const ALL_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  ...BANNER_TRANSLATION_FIELDS,
  ...MISSION_TRANSLATION_FIELDS,
  ...STATS_TRANSLATION_FIELDS,
  ...SPHERES_TRANSLATION_FIELDS,
  ...EXPERIENCE_TRANSLATION_FIELDS,
  ...METHODOLOGY_TRANSLATION_FIELDS,
  ...DESCRIPTION_TRANSLATION_FIELDS,
];

/** Все ключи переводов для buildFormState / handleSubmit */
const ALL_TRANSLATION_KEYS = ALL_TRANSLATION_FIELDS.map((f) => f.name);

// ─── Helpers ──────────────────────────────────────

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
};

interface TeacherRecord {
  id: number | string;
  translations?: { languageCode: string; name: string }[];
}

function getTeacherName(teacher: TeacherRecord): string {
  if (!teacher.translations || teacher.translations.length === 0) {return `#${teacher.id}`;}
  const ru = teacher.translations.find((t) => t.languageCode === 'ru');
  return ru?.name ?? teacher.translations[0]?.name ?? `#${teacher.id}`;
}

interface FileRecord {
  id: number;
  url: string;
}

const DEFAULT_STATS: ClubStats = {
  researchCount: 0,
  practiceHours: 0,
  specialistHours: 0,
  countriesCount: 0,
};

interface ClubSettingsFormState {
  bannerMobile: FileRecord | null;
  bannerTablet: FileRecord | null;
  bannerDesktop: FileRecord | null;
  missionAuthorTeacherId: number | null;
  stats: ClubStats;
  spheres: ClubSphere[];
  experienceYears: number;
  experienceImage: FileRecord | null;
  translations: TranslationData;
}

function buildFileRecord(fileId: number | null, url: string | null): FileRecord | null {
  if (!fileId) { return null; }
  return { id: fileId, url: url ?? `/uploads/public/${fileId}` };
}

function buildFormState(data?: ClubSettings): ClubSettingsFormState {
  const baseState: ClubSettingsFormState = {
    bannerMobile: null,
    bannerTablet: null,
    bannerDesktop: null,
    missionAuthorTeacherId: null,
    stats: DEFAULT_STATS,
    spheres: [],
    experienceYears: 0,
    experienceImage: null,
    translations: createEmptyTranslationData(),
  };

  if (!data) {
    return baseState;
  }

  const translations = createEmptyTranslationData();

  for (const t of data.translations ?? []) {
    const row: Record<string, string> = {};
    for (const key of ALL_TRANSLATION_KEYS) {
      const typedKey = key as keyof ClubSettingsTranslation;
      const val = t[typedKey];
      row[key] = (typeof val === 'string' ? val : null) ?? '';
    }
    translations[t.languageCode] = row;
  }

  return {
    bannerMobile: buildFileRecord(data.bannerMobileFileId, data.bannerImages?.mobile ?? null),
    bannerTablet: buildFileRecord(data.bannerTabletFileId, data.bannerImages?.tablet ?? null),
    bannerDesktop: buildFileRecord(data.bannerDesktopFileId, data.bannerImages?.desktop ?? null),
    missionAuthorTeacherId: data.missionAuthorTeacherId ?? null,
    stats: data.stats ?? DEFAULT_STATS,
    spheres: data.spheres ?? [],
    experienceYears: data.experienceYears ?? 0,
    experienceImage: data.experienceImageId && data.experienceImageUrl
      ? { id: data.experienceImageId, url: data.experienceImageUrl }
      : data.experienceImageUrl ? { id: 0, url: data.experienceImageUrl } : null,
    translations,
  };
}

// ─── Main component ──────────────────────────────────────

interface ClubSettingsApiResponse {
  settings: Record<string, unknown> | null;
  translations: ClubSettings['translations'];
}

export function ClubSettingsForm() {
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['club-settings', 'singleton'],
    queryFn: () => apiClient.get<ClubSettingsApiResponse>('/admin/club-settings/singleton'),
  });

  const updateMutation = useUpdate<ClubSettings>();
  const update = updateMutation.mutate;
  const isUpdating = updateMutation.mutation?.isPending ?? false;

  const { result: languagesResult, query: langQuery } = useList<LanguageRecord>({
    resource: 'languages',
    pagination: { pageSize: 100 },
  });
  const isLanguagesLoading = langQuery?.isLoading ?? false;

  const { result: teachersResult } = useList<TeacherRecord>({
    resource: 'teachers',
    pagination: { pageSize: 100 },
    filters: [{ field: 'isActive', operator: 'eq', value: true }],
  });

  const teachers = teachersResult?.data ?? [];

  const languages: TranslationLanguage[] | undefined =
    languagesResult?.data?.map((l) => ({
      code: l.code,
      name: l.name,
      nativeName: l.nativeName,
      flag: l.flag || l.flagEmoji || '',
    }));

  // apiClient.get возвращает { settings, translations } напрямую
  const clubSettings: ClubSettings | undefined = rawData?.settings
    ? { ...rawData.settings, translations: rawData.translations ?? [] } as ClubSettings
    : undefined;
  const initialState = buildFormState(clubSettings);
  const formKey = clubSettings ? `loaded-${clubSettings.id}` : 'loading';

  if (isLoading || isLanguagesLoading || !clubSettings) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <ClubSettingsFormFields
      key={formKey}
      initialState={initialState}
      isUpdating={isUpdating}
      languages={languages}
      teachers={teachers}
      onSave={(payload) => {
        update({
          resource: 'club-settings',
          id: 'singleton',
          values: payload,
        });
      }}
    />
  );
}

// ─── Form fields ──────────────────────────────────────

interface ClubSettingsFormFieldsProps {
  initialState: ClubSettingsFormState;
  isUpdating: boolean;
  languages?: TranslationLanguage[];
  teachers: TeacherRecord[];
  onSave: (payload: ClubSettingsFormData) => void;
}

function ClubSettingsFormFields({
  initialState,
  isUpdating,
  languages,
  teachers,
  onSave,
}: ClubSettingsFormFieldsProps) {
  const [bannerMobile, setBannerMobile] = useState<FileRecord | null>(initialState.bannerMobile);
  const [bannerTablet, setBannerTablet] = useState<FileRecord | null>(initialState.bannerTablet);
  const [bannerDesktop, setBannerDesktop] = useState<FileRecord | null>(initialState.bannerDesktop);
  const [missionAuthorTeacherId, setMissionAuthorTeacherId] = useState<number | null>(
    initialState.missionAuthorTeacherId
  );
  const [stats, setStats] = useState<ClubStats>(initialState.stats);
  const [experienceYears, setExperienceYears] = useState<number>(initialState.experienceYears);
  const [experienceImage, setExperienceImage] = useState<FileRecord | null>(initialState.experienceImage);
  const [translations, setTranslations] = useState<TranslationData>(initialState.translations);
  const [activeLanguage, setActiveLanguage] = useState<LanguageCode>('ru');

  const handleSubmit = () => {
    const langCodes = languages?.map((l) => l.code) ??
      Object.keys(translations).filter((k) => translations[k]?.title);

    const translationsPayload = langCodes.map((code) => {
      const t = translations[code] ?? {};
      const row: Record<string, string | null> = { languageCode: code };
      for (const key of ALL_TRANSLATION_KEYS) {
        row[key] = key === 'title' ? (t[key] ?? '') : (t[key] || null);
      }
      return row as ClubSettingsFormData['translations'][number];
    }).filter((t) => t.title);

    const payload: ClubSettingsFormData = {
      bannerImages: (bannerMobile || bannerTablet || bannerDesktop)
        ? {
            mobile: bannerMobile?.url ?? '',
            tablet: bannerTablet?.url ?? '',
            desktop: bannerDesktop?.url ?? '',
          }
        : null,
      bannerMobileFileId: bannerMobile?.id ?? null,
      bannerTabletFileId: bannerTablet?.id ?? null,
      bannerDesktopFileId: bannerDesktop?.id ?? null,
      missionAuthorTeacherId,
      stats,
      spheres: initialState.spheres,
      experienceYears,
      experienceImageUrl: experienceImage?.url ?? null,
      experienceImageId: experienceImage?.id ?? null,
      translations: translationsPayload,
    };

    onSave(payload);
  };

  const handleStatChange = (field: keyof ClubStats, value: string) => {
    const num = parseInt(value, 10);
    setStats((prev) => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };

  const translationsFormProps = {
    value: translations,
    onChange: setTranslations,
    languages,
    activeLanguage,
    onLanguageChange: (code: string) => setActiveLanguage(code as LanguageCode),
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title="Страница о Клубе"
        sticky
        actions={
          languages && languages.length > 0 ? (
            <LanguageSwitcher
              languages={languages}
              activeLanguage={activeLanguage}
              onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
              fields={ALL_TRANSLATION_FIELDS}
              value={translations}
              variant="compact"
            />
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Левая колонка — основной контент */}
        <div className="lg:col-span-2 space-y-6">
          {/* Баннер */}
          <Card>
            <CardHeader>
              <CardTitle>Баннер</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="Mobile" name="bannerMobile" hint="640px, 4:3">
                  <FileImageUpload value={bannerMobile} onChange={setBannerMobile} aspectRatio="4/3" />
                </FormField>
                <FormField label="Tablet" name="bannerTablet" hint="1024px, 16:9">
                  <FileImageUpload value={bannerTablet} onChange={setBannerTablet} aspectRatio="16/9" />
                </FormField>
                <FormField label="Desktop" name="bannerDesktop" hint="1440px, 21:9">
                  <FileImageUpload value={bannerDesktop} onChange={setBannerDesktop} aspectRatio="21/9" />
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* Надписи баннера */}
          <Card>
            <CardHeader><CardTitle>Надписи баннера</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={BANNER_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>

          {/* Миссия клуба */}
          <Card>
            <CardHeader><CardTitle>Миссия клуба</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={MISSION_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>

          {/* Статистика — числа */}
          <Card>
            <CardHeader><CardTitle>Статистика — числа</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Исследований</Label>
                  <Input
                    type="number"
                    value={stats.researchCount}
                    onChange={(e) => handleStatChange('researchCount', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Часов практики</Label>
                  <Input
                    type="number"
                    value={stats.practiceHours}
                    onChange={(e) => handleStatChange('practiceHours', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Часов работы специалистов</Label>
                  <Input
                    type="number"
                    value={stats.specialistHours}
                    onChange={(e) => handleStatChange('specialistHours', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Стран присутствия</Label>
                  <Input
                    type="number"
                    value={stats.countriesCount}
                    onChange={(e) => handleStatChange('countriesCount', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Статистика — подписи */}
          <Card>
            <CardHeader><CardTitle>Статистика — подписи</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={STATS_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>

          {/* Сферы жизни — надписи */}
          <Card>
            <CardHeader><CardTitle>Сферы жизни — надписи</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={SPHERES_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>

          {/* Опыт клуба — надписи */}
          <Card>
            <CardHeader><CardTitle>Опыт клуба — надписи</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={EXPERIENCE_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>

          {/* Методология */}
          <Card>
            <CardHeader><CardTitle>Методология</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={METHODOLOGY_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>

          {/* Описание клуба */}
          <Card>
            <CardHeader><CardTitle>Описание клуба</CardTitle></CardHeader>
            <CardContent>
              <TranslationsForm fields={DESCRIPTION_TRANSLATION_FIELDS} {...translationsFormProps} />
            </CardContent>
          </Card>
        </div>

        {/* Правая колонка */}
        <div className="space-y-6">
          {/* Автор миссии */}
          <Card>
            <CardHeader><CardTitle>Автор миссии</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Учитель</Label>
                <Select
                  value={missionAuthorTeacherId ? String(missionAuthorTeacherId) : ''}
                  onValueChange={(value) => {
                    setMissionAuthorTeacherId(value === '__clear__' ? null : Number(value));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите учителя" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__clear__">— Не выбран —</SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem key={String(teacher.id)} value={String(teacher.id)}>
                        {getTeacherName(teacher)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Фото и имя учителя будут отображаться в блоке миссии на странице о клубе
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Опыт клуба — данные */}
          <Card>
            <CardHeader><CardTitle>Опыт клуба</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Количество лет</Label>
                <Input
                  type="number"
                  value={experienceYears}
                  onChange={(e) => {
                    const num = parseInt(e.target.value, 10);
                    setExperienceYears(isNaN(num) ? 0 : num);
                  }}
                />
              </div>
              <FormField label="Изображение опыта" name="experienceImage">
                <FileImageUpload
                  value={experienceImage}
                  onChange={setExperienceImage}
                  aspectRatio="1/1"
                />
              </FormField>
            </CardContent>
          </Card>
        </div>
      </div>

      <FormSpacer />

      <FormStickyActions>
        <Button
          onClick={handleSubmit}
          disabled={isUpdating}
        >
          {isUpdating ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </FormStickyActions>
    </div>
  );
}
